import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router'
import { formatDate } from '@/dayjs'
import {
	AssignHostDialog,
	type AssignHostTarget
} from '@/features/marquee/components/assign-host-dialog'
import { GenerateExportCard } from '@/features/marquee/components/export-generate-card'
import { ExportHeader } from '@/features/marquee/components/export-header'
import { LastExportBanner } from '@/features/marquee/components/export-last-banner'
import { ExportOwnerBanner } from '@/features/marquee/components/export-owner-banner'
import { ExportPreviewTable } from '@/features/marquee/components/export-preview-table'
import { ExportResolvedBanner } from '@/features/marquee/components/export-resolved-banner'
import {
	ExportError,
	ExportLoading,
	ExportPageShell,
	ExportUnavailable
} from '@/features/marquee/components/export-states'
import {
	ExportValidationGate,
	type GateItem,
	GateListTooltip
} from '@/features/marquee/components/export-validation-gate'
import {
	MarkUnresolvableDialog,
	type UnresolvableTarget
} from '@/features/marquee/components/mark-unresolvable-dialog'
import { MarqueeTableCard } from '@/features/marquee/components/marquee-table-card'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	eventQueryOptions,
	exportPreviewQueryOptions,
	operatorQueryOptions
} from '@/features/marquee/query-options'
import {
	invalidStateMessage,
	isExportRowUnresolved,
	isExportRowWalkIn,
	isMarketManagerAllowed,
	saveCsv,
	splitExportAttendee,
	unmappedExportCtaLabels
} from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import {
	exportLatestCsv,
	type MarqueeExportPreview,
	type MarqueeExportRow,
	restoreResolution,
	runExport
} from '@/sdk'
import { Trans, useTranslation } from '@/translations/i18n'

/**
 * Derives the 3 validation-gate checklist items from the structural gate
 * booleans and the preview counts, interpolating the live numbers into the
 * (static i18n) copy.
 *
 * The resolution item is one slot with three states, per design: pending rows
 * block (warning), rows already given up on are stated for the record (info,
 * not blocking), and a clean lane reads as passed. It covers the unified
 * "unresolved" set (walk-ins + invitees) via `unresolvedCount`, the single
 * number shared with the banner and the download card.
 *
 * A fourth item appears only when the backend names CTAs with no Salesforce
 * Call Topic mapping (`counts.unmapped_call_topics`): the run drops them from
 * the Call Topics cell and exports the rest, so it is stated as info, never as
 * a blocker, with the full list in a tooltip on the count (the grid's CTA
 * tooltip, so the two read alike). Absent from the checklist when there is
 * nothing to name, since the design draws three checks.
 */
const useGateItems = (
	preview: MarqueeExportPreview | undefined,
	unresolvedCount: number,
	resolvedWalkInCount: number
): GateItem[] => {
	const { t } = useTranslation()
	const gate = preview?.gate
	const included = preview?.counts?.included ?? 0
	const excluded = preview?.counts?.excluded ?? 0
	// Rows that would ship a blank WhatId column, which is what the structural
	// item reports on. Derived from the grid rather than read off `counts.failed`:
	// that field counts *failing gate assertions*, not rows, so rendering it here
	// restated whichever check had already turned red one item above.
	const structuralGaps =
		preview?.rows?.filter(
			(row) => row.disposition === 'included' && !row.child_account
		).length ?? 0

	return useMemo(() => {
		if (!gate) {
			return []
		}
		// Derived in here rather than above: the label list is an array, so
		// building it per render would give this memo a fresh dependency each
		// time. The preview is the only input it reads.
		const unmappedCtas = unmappedExportCtaLabels(preview)
		// Picks the ok/failed copy for one checklist item and derives its tone.
		const item = (
			id: string,
			failed: boolean,
			ok: { title: string; detail: string },
			bad: { title: string; detail: string }
		): GateItem => ({
			id,
			tone: failed ? 'warning' : 'ok',
			title: failed ? bad.title : ok.title,
			detail: failed ? bad.detail : ok.detail
		})

		const resolutionItem = (): GateItem => {
			if (unresolvedCount > 0) {
				return {
					id: 'resolution',
					tone: 'warning',
					title: t('marquee:export_gate_pending_title', {
						count: unresolvedCount
					}),
					detail: t('marquee:export_gate_pending_detail')
				}
			}
			if (excluded > 0) {
				return {
					id: 'resolution',
					tone: 'info',
					title: t('marquee:export_gate_excluded_title', {
						count: excluded
					}),
					detail: t('marquee:export_gate_excluded_detail')
				}
			}
			return {
				id: 'resolution',
				tone: 'ok',
				title: t('marquee:export_gate_accounts_title'),
				detail:
					resolvedWalkInCount > 0
						? t('marquee:export_gate_accounts_detail_split', {
								invitees: included - resolvedWalkInCount,
								walkIns: resolvedWalkInCount
							})
						: t('marquee:export_gate_accounts_detail', {
								included
							})
			}
		}

		const unmappedItem: GateItem[] =
			unmappedCtas.length > 0
				? [
						{
							id: 'unmapped',
							tone: 'info',
							title: (
								<Trans
									components={{
										ctas: (
											<GateListTooltip
												entries={unmappedCtas}
											/>
										)
									}}
									count={unmappedCtas.length}
									i18nKey='marquee:export_gate_unmapped_title'
								/>
							),
							detail: t('marquee:export_gate_unmapped_detail')
						}
					]
				: []

		return [
			item(
				'ctas',
				gate.ctas_in_cta_library === false,
				{
					title: t('marquee:export_gate_ctas_title'),
					detail: t('marquee:export_gate_ctas_detail')
				},
				{
					title: t('marquee:export_gate_ctas_title_fail'),
					detail: t('marquee:export_gate_ctas_detail_fail')
				}
			),
			resolutionItem(),
			// The design only draws this one green, and the resolver agrees: a
			// row with no child account is skipped, never included, so the
			// assertion behind it is a should-never-happen invariant rather
			// than a state the operator can walk into. The failed copy has no
			// mock for that reason, but stays wired as the safety net for the
			// day the invariant breaks.
			item(
				'structure',
				gate.missing_host_excluded === false,
				{
					title: t('marquee:export_gate_structure_title'),
					detail: t('marquee:export_gate_structure_detail')
				},
				{
					title: t('marquee:export_gate_structure_title_fail', {
						count: structuralGaps
					}),
					detail: t('marquee:export_gate_structure_detail_fail')
				}
			),
			...unmappedItem
		]
	}, [
		gate,
		included,
		excluded,
		structuralGaps,
		unresolvedCount,
		resolvedWalkInCount,
		preview,
		t
	])
}

/**
 * Preview-grid heading: "N ready", plus the pending remainder if the run is
 * still blocked, otherwise the count given up on.
 */
function usePreviewTitle(included: number, pending: number, excluded: number) {
	const { t } = useTranslation()
	if (pending > 0) {
		return t('marquee:export_preview_title_pending', {
			ready: included,
			count: pending
		})
	}
	if (excluded > 0) {
		return t('marquee:export_preview_title_excluded', {
			ready: included,
			count: excluded
		})
	}
	return t('marquee:export_preview_title_ready', { ready: included })
}

function MarqueeEventExport() {
	const { t } = useTranslation()
	const { id } = useParams<{ id: string }>()

	const { data: event } = useQuery(eventQueryOptions(id))
	const isClosed = event?.state === 'closed'

	// Generating the export is creator-only; every export read is open. So a
	// facilitator keeps the preview, the validation gate and the re-download of a
	// finished run, and loses only the button that starts one.
	const { data: operator } = useQuery(operatorQueryOptions())
	const showGenerateCard = isMarketManagerAllowed(operator, 'runExport')

	// Only meaningful once the event has closed, on top of the factory's own
	// "do we have an id yet" gate.
	const options = exportPreviewQueryOptions(id)
	const {
		data: preview,
		isLoading,
		isError
	} = useQuery({ ...options, enabled: options.enabled && isClosed })

	const queryClient = useQueryClient()

	/**
	 * Walks back an unresolvable mark, putting the row back in the pending lane.
	 *
	 * The mark is the operator's verdict rather than a fact, so it has to be
	 * correctable, and this is the only place that corrects it: the check-in
	 * roster shows marked rows but offers nothing on them, so mark and unmark sit
	 * together here. No confirmation, unlike the mark: this direction only
	 * re-blocks a gate the operator can clear again.
	 *
	 * Restoring re-blocks the export gate, so the roster, the gate and the
	 * preview all change: one event prefix covers them.
	 */
	const restore = useMutation({
		mutationFn: (attendeeId: string) =>
			restoreResolution({ path: { attendeeId } }),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(id ?? '')
			}),
		onError: (error) => {
			// `restore-resolution` shares the 409 `invalid-state` code with six
			// other writes, so the backend's sentence names no action. Only
			// `locked` refuses this one, which is what the message says.
			const refusal = invalidStateMessage({
				error,
				action: 'restore_resolution',
				eventState: event?.state
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			errorHandler(error)
		}
	})

	/**
	 * Two requests, deliberately. `POST /export` content-negotiates on the
	 * `Prefer` header, not on the body: without `Prefer: json` it streams the raw
	 * CSV, with it, it returns the run record. We ask for the record because it
	 * carries `filename` (`<hashtag>-market-visit-<timestamp>.csv`), then fetch
	 * the bytes from `GET /export/csv`, which serves the run's stored content.
	 * The download responses carry no `content-disposition`, so the record is the
	 * only source of the real filename.
	 */
	const exportRun = useMutation({
		mutationFn: async () => {
			const run = await runExport({
				path: { eventId: id ?? '' },
				body: { sink: 'csv' },
				headers: { Prefer: 'json' }
			})
			const csv = await exportLatestCsv({ path: { eventId: id ?? '' } })
			return {
				csv: csv.data,
				filename:
					run.data.record?.filename ?? `marquee-export-${id}.csv`
			}
		},
		onSuccess: async ({ csv, filename }) => {
			saveCsv(filename, csv)
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.exportPreview(id ?? '')
			})
		},
		// The run returns JSON on failure too (the gate 409 included), so the
		// standard handler reads `message` directly.
		onError: (error) => errorHandler(error)
	})

	/**
	 * Re-download of the last generated CSV, served from the stored content
	 * without re-running, so it keeps working even if a new pending walk-in
	 * appears afterwards. The filename comes from the export record for the same
	 * reason as above.
	 */
	const redownload = useMutation({
		mutationFn: async () => {
			const response = await exportLatestCsv({
				path: { eventId: id ?? '' }
			})
			return response.data
		},
		onSuccess: (csv) =>
			saveCsv(
				preview?.last_export?.filename ?? `marquee-export-${id}.csv`,
				csv
			),
		onError: (error) => errorHandler(error)
	})

	const [unresolvableTarget, setUnresolvableTarget] =
		useState<UnresolvableTarget | null>(null)
	const [assignHostTarget, setAssignHostTarget] =
		useState<AssignHostTarget | null>(null)

	// Single source of truth for the actionable "needs resolution" set: walk-ins
	// AND invitees alike (unified UX). Drives the banner, gate, generate card and
	// header flag so every count on the tab agrees.
	const rows = useMemo(() => preview?.rows ?? [], [preview])
	const unresolvedCount = useMemo(
		() => rows.filter(isExportRowUnresolved).length,
		[rows]
	)
	// Terminal rows: counted for the operator, but never a blocker. The preview
	// counts them itself, in their own lane, so this is not re-derived from the
	// grid: `counts` is the whole run, the rows are what the grid renders.
	const excludedCount = preview?.counts?.excluded ?? 0

	// Walk-ins that made it into the run: counted in the resolution banner, and
	// the gate's passed detail splits the included tally into invitees +
	// walk-ins.
	const resolvedWalkIns = useMemo(
		() =>
			rows.filter(
				(row) =>
					row.disposition === 'included' && isExportRowWalkIn(row)
			),
		[rows]
	)
	// Included rows that will export with empty owner columns. Strict null on
	// purpose: an omitted field means "not reported", not "no owner".
	const ownerGapCount = useMemo(
		() =>
			rows.filter(
				(row) => row.disposition === 'included' && row.owner_id === null
			).length,
		[rows]
	)

	const gateItems = useGateItems(
		preview,
		unresolvedCount,
		resolvedWalkIns.length
	)
	const previewTitle = usePreviewTitle(
		preview?.counts?.included ?? 0,
		unresolvedCount,
		excludedCount
	)

	const included = preview?.counts?.included ?? 0
	// A generated CSV collapses the top into a single summary banner and swaps
	// the preview grid for the post-download "load status" view (host set frozen
	// once closed, so no pending can reappear).
	const lastExport = preview?.last_export
	const gateBlocked =
		!!preview?.gate &&
		Object.values(preview.gate).some((value) => value === false)

	const handleMarkUnresolvable = (row: MarqueeExportRow) => {
		if (row.attendee_id) {
			setUnresolvableTarget({
				attendeeId: row.attendee_id,
				name: splitExportAttendee(row.attendee).name,
				hostId: row.host_id ?? undefined
			})
		}
	}

	const handleAssignHost = (row: MarqueeExportRow) => {
		if (row.attendee_id) {
			setAssignHostTarget({
				attendeeId: row.attendee_id,
				name: splitExportAttendee(row.attendee).name
			})
		}
	}

	const handleRestoreResolution = (row: MarqueeExportRow) => {
		if (row.attendee_id) {
			restore.mutate(row.attendee_id)
		}
	}

	// Export is only meaningful once the event has closed.
	if (event && !isClosed) {
		return <ExportUnavailable />
	}

	if (isLoading || !event) {
		return <ExportLoading />
	}

	if (isError || !preview) {
		return <ExportError />
	}

	return (
		<ExportPageShell>
			<ExportHeader
				attendeeCount={rows.length}
				pendingCount={unresolvedCount}
				record={lastExport}
				timeZone={event?.time_zone}
			/>

			{lastExport ? (
				/* Post-download: one summary banner replaces gate + generate */
				<LastExportBanner
					isRedownloading={redownload.isPending}
					onRedownload={() => redownload.mutate()}
					record={lastExport}
					timeZone={event?.time_zone}
				/>
			) : (
				<>
					{ownerGapCount > 0 && (
						<ExportOwnerBanner count={ownerGapCount} />
					)}
					{unresolvedCount === 0 && (
						<ExportResolvedBanner
							excludedCount={excludedCount}
							resolvedCount={resolvedWalkIns.length}
						/>
					)}
					{showGenerateCard ? (
						<div className='grid l:grid-cols-[1.6fr_1fr] gap-6 l:gap-4'>
							<ExportValidationGate items={gateItems} />
							<GenerateExportCard
								excludedCount={excludedCount}
								gateBlocked={gateBlocked}
								included={included}
								isExporting={exportRun.isPending}
								onExport={() => exportRun.mutate()}
								pendingCount={unresolvedCount}
							/>
						</div>
					) : (
						/* The gate still earns its place without the button: it
						   lists exactly the rows a facilitator can go and fix. */
						<ExportValidationGate items={gateItems} />
					)}
				</>
			)}

			<MarqueeTableCard
				header={
					<h2 className='font-semibold text-lg text-neutral-900'>
						{previewTitle}
					</h2>
				}
			>
				<ExportPreviewTable
					captureMode={event?.capture_mode}
					data={rows}
					eventId={id}
					loadedAt={
						lastExport?.exported_at
							? formatDate(
									lastExport.exported_at,
									'HH:mm:ss',
									event?.time_zone
								)
							: undefined
					}
					onAssignHost={handleAssignHost}
					onMarkUnresolvable={handleMarkUnresolvable}
					onRestoreResolution={handleRestoreResolution}
					restoringAttendeeId={
						restore.isPending ? restore.variables : null
					}
					variant={lastExport ? 'loaded' : 'preview'}
				/>
			</MarqueeTableCard>

			{/* The confirmation opens over the search rather than replacing it:
			    cancelling there is "no, let me keep looking", so it has to land
			    back on the query and the selection it left. Only the mark itself
			    ends the errand, and closes both. */}
			<AssignHostDialog
				eventId={id}
				eventState={event?.state}
				onClose={() => setAssignHostTarget(null)}
				onMarkUnresolvable={(handover) =>
					setUnresolvableTarget({
						attendeeId: handover.attendeeId,
						name: handover.name
					})
				}
				target={assignHostTarget}
			/>

			<MarkUnresolvableDialog
				eventId={id}
				eventState={event?.state}
				onClose={() => setUnresolvableTarget(null)}
				onMarked={() => setAssignHostTarget(null)}
				target={unresolvableTarget}
			/>
		</ExportPageShell>
	)
}

export { MarqueeEventExport }
